/**
 * Product Group Detail Page - Refactored
 * Uses ProductShowcase shared component with image always on left
 * Additional features: SEO, breadcrumbs, horizontal media section, related products
 */
import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useRegion } from '@/context/RegionContext';
import { useProductDetail, useCategories, useRelatedProducts } from '@/hooks/useApiQueries';
import { resolveImageUrl, API_URL } from '@/lib/api';
import { Breadcrumb, HorizontalMediaSection } from '@/components/product/ProductDetailComponents';
import RelatedProducts from '@/components/product/RelatedProducts';
import { ProductCardSkeleton } from '@/components/Skeletons';
import SEO from '@/components/SEO';
import SocialShareButtons from '@/components/SocialShareButtons';
import ProductShowcase from '@/components/ProductShowcase';
import AdPlaceholder from '@/components/AdPlaceholder';

// Helpers
const isVideo = (url) => url && /\.(mp4|mov|webm|avi|mkv)$/i.test(url);
const getMediaTagLabel = (type) => type === 'ai-edit' ? 'AI-Edit' : type === 'creator' ? 'Creator media' : null;

const ProductGroupDetailPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const fullPath = location.pathname.replace('/product/', '');
  const pathSegments = fullPath.split('/').filter(Boolean);
  const slug = pathSegments[pathSegments.length - 1] || pathSegments[0];
  
  const { region } = useRegion();
  const { data: productData, isLoading: productLoading, error: productError, isError } = useProductDetail(fullPath);
  const { data: categories = [] } = useCategories();

  const [selectedHorizontalIndex, setSelectedHorizontalIndex] = useState(0);
  const [isHorizontalTextOverflowing, setIsHorizontalTextOverflowing] = useState(false);
  const horizontalTextRef = useRef(null);
  const horizontalMediaRef = useRef(null);

  // Scroll to top when navigating to this page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Fetch related products based on the product's configuration
  const { data: relatedData } = useRelatedProducts(productData?.id, region);

  // Handle redirect if path doesn't match canonical URL
  useEffect(() => {
    if (productData?.should_redirect && productData?.canonical_url) {
      navigate(productData.canonical_url, { replace: true });
    }
  }, [productData, navigate]);

  // Track page view
  useEffect(() => {
    if (productData?.id) {
      fetch(`${API_URL}/api/analytics/track/view/${productData.id}`, {
        method: 'POST'
      }).catch(() => {});
    }
  }, [productData?.id]);

  // Get the actual group data
  const group = productData?.should_redirect ? null : productData;
  const loading = productLoading;

  // Redirect to search page if product not found (404 error)
  useEffect(() => {
    if (isError && !loading) {
      navigate('/search', { replace: true });
    }
  }, [isError, loading, navigate]);

  // Dynamic layout for horizontal media text
  // Desktop: Text constrained to media height
  // Mobile: Text constrained to 7 lines (~182px)
  const MOBILE_MAX_LINES = 7;
  const MOBILE_LINE_HEIGHT = 26;
  const MOBILE_MAX_HEIGHT = MOBILE_MAX_LINES * MOBILE_LINE_HEIGHT;

  useEffect(() => {
    if (!group) return;
    
    const calculateHorizontalLayout = () => {
      const textEl = horizontalTextRef.current;
      const mediaEl = horizontalMediaRef.current;
      
      if (!textEl) return;
      
      // Clear styles to measure natural height
      textEl.style.maxHeight = '';
      textEl.style.overflowY = '';
      void textEl.offsetHeight;
      
      const textNaturalHeight = textEl.scrollHeight;
      
      // Mobile: text comes first, constrain to 7 lines
      if (window.innerWidth < 1024) {
        if (textNaturalHeight > MOBILE_MAX_HEIGHT) {
          textEl.style.maxHeight = `${MOBILE_MAX_HEIGHT}px`;
          textEl.style.overflowY = 'auto';
          setIsHorizontalTextOverflowing(true);
        } else {
          setIsHorizontalTextOverflowing(false);
        }
        return;
      }
      
      // Desktop: text side-by-side with media, constrain to actual media height
      if (!mediaEl || mediaEl.offsetHeight === 0) {
        // Media hasn't loaded yet, don't constrain text
        setIsHorizontalTextOverflowing(false);
        return;
      }
      
      const mediaHeight = mediaEl.offsetHeight;
      
      if (textNaturalHeight <= mediaHeight) {
        textEl.style.maxHeight = '';
        setIsHorizontalTextOverflowing(false);
      } else {
        textEl.style.maxHeight = `${mediaHeight}px`;
        textEl.style.overflowY = 'auto';
        setIsHorizontalTextOverflowing(true);
      }
    };
    
    // Initial calculation with delay for images/video to load
    const timer = setTimeout(calculateHorizontalLayout, 300);
    
    // Polling for media dimensions (images/videos may take time to load)
    let pollCount = 0;
    const maxPolls = 30; // More polls for slower loading media
    const pollInterval = setInterval(() => {
      pollCount++;
      const mediaEl = horizontalMediaRef.current;
      // Keep polling until media has height or we've tried enough
      if (mediaEl && mediaEl.offsetHeight > 50) {
        calculateHorizontalLayout();
        // Don't stop polling - keep recalculating as media might resize
        if (pollCount >= maxPolls) {
          clearInterval(pollInterval);
        }
      } else if (pollCount >= maxPolls) {
        calculateHorizontalLayout();
        clearInterval(pollInterval);
      }
    }, 150);
    
    // Throttled resize handler
    let resizeTimeout = null;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(calculateHorizontalLayout, 100);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Breakpoint listener for immediate recalculation
    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    const handleBreakpointChange = () => {
      setTimeout(calculateHorizontalLayout, 50);
      setTimeout(calculateHorizontalLayout, 200);
    };
    mediaQuery.addEventListener('change', handleBreakpointChange);
    
    // Handle media load events
    const handleMediaLoad = () => setTimeout(calculateHorizontalLayout, 100);
    const mediaContainer = horizontalMediaRef.current;
    const mediaElements = mediaContainer?.querySelectorAll('img, video') || [];
    mediaElements.forEach(el => {
      if (el.tagName === 'IMG' && !el.complete) {
        el.addEventListener('load', handleMediaLoad);
      }
      if (el.tagName === 'VIDEO') {
        el.addEventListener('loadedmetadata', handleMediaLoad);
        el.addEventListener('loadeddata', handleMediaLoad);
        el.addEventListener('canplay', handleMediaLoad);
      }
    });
    
    return () => {
      clearTimeout(timer);
      clearTimeout(resizeTimeout);
      clearInterval(pollInterval);
      window.removeEventListener('resize', handleResize);
      mediaQuery.removeEventListener('change', handleBreakpointChange);
      mediaElements.forEach(el => {
        el.removeEventListener('load', handleMediaLoad);
        el.removeEventListener('loadedmetadata', handleMediaLoad);
        el.removeEventListener('loadeddata', handleMediaLoad);
        el.removeEventListener('canplay', handleMediaLoad);
      });
    };
  }, [group, selectedHorizontalIndex]);

  if (loading) return <div className="px-4 py-8"><ProductCardSkeleton /></div>;
  if (isError || !group) return null; // Will redirect via useEffect

  // Derived values
  const seoDescription = (group.seo_description || group.meta_description || group.description || '').substring(0, 160);

  // Horizontal media with types
  const horizontalMedia = (group.horizontal_media || []).map((m, idx) => {
    const url = typeof m === 'string' ? m : m.url;
    const type = (group.horizontal_media_types || [])[idx] || '';
    return { url: resolveImageUrl(url), type };
  });

  const getCategoryTitle = (slug) => categories.find(c => c.id === slug || c.slug === slug)?.title || slug;
  const getSubcategoryTitle = (catSlug, subSlug) => {
    const cat = categories.find(c => c.id === catSlug || c.slug === catSlug);
    return cat?.subcategories?.find(s => s.id === subSlug || s.slug === subSlug)?.name || subSlug?.replace(/-/g, ' ');
  };

  const handleShareTracked = (platform) => {
    fetch(`${API_URL}/api/analytics/track/share/${group.id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ platform })
    }).catch(() => {});
  };

  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'Home', to: '/' },
    { label: getCategoryTitle(group.category), to: `/${group.category}` },
    ...(group.subcategory ? [{ label: getSubcategoryTitle(group.category, group.subcategory), to: `/${group.category}/${group.subcategory}` }] : []),
    { label: group.title }
  ];

  // SEO breadcrumbs
  const seoBreadcrumbs = [
    { name: 'Home', url: '/' },
    { name: getCategoryTitle(group.category), url: `/${group.category}` },
    ...(group.subcategory ? [{ name: getSubcategoryTitle(group.category, group.subcategory), url: `/${group.category}/${group.subcategory}` }] : []),
    { name: group.title }
  ];

  // Social share buttons component to pass as titleExtra
  const shareButtons = (
    <SocialShareButtons 
      title={group.title}
      description={seoDescription}
      url={window.location.href}
      image={resolveImageUrl(group.image_url)}
      onShareTracked={handleShareTracked}
    />
  );

  return (
    <>
      <SEO 
        title={group.title}
        description={group.description || `Shop ${group.title} - Curated Amazon finds for the cool girls.`}
        image={resolveImageUrl(group.image_url)}
        url={`/${group.category}/${group.subcategory}/${slug}`}
        type="product"
        product={{
          name: group.title,
          description: group.description,
          image: resolveImageUrl(group.image_url),
          price: group.products?.[0]?.price,
          currency: region === 'uk' ? 'GBP' : 'USD'
        }}
        breadcrumbs={seoBreadcrumbs}
      />

      <div className="bg-site-bg" data-testid="product-group-detail-page">
        <Breadcrumb items={breadcrumbItems} />

        <div className="px-10 md:px-20 lg:px-28 xl:px-36 pt-4 pb-8">
          <div className="max-w-[1400px] mx-auto">
            {/* Main Product Showcase - thumbnails included for proper height calculation */}
            <ProductShowcase
              group={group}
              categories={categories}
              imagePosition="left"
              showDetailButton={false}
              showShopButton={true}
              isLinkable={false}
              titleExtra={shareButtons}
              showThumbnailsBelowImage={true}
            />

            {/* Horizontal Media Section */}
            <HorizontalMediaSection
              media={horizontalMedia}
              text={group.horizontal_media_text}
              selectedIndex={selectedHorizontalIndex}
              onSelect={setSelectedHorizontalIndex}
              isOverflowing={isHorizontalTextOverflowing}
              textRef={horizontalTextRef}
              mediaRef={horizontalMediaRef}
              getMediaTagLabel={getMediaTagLabel}
              isVideo={isVideo}
            />

            {/* Ad between media and related products */}
            <div className="px-6 md:px-12 lg:px-24 py-6">
              <AdPlaceholder slot="in-feed" />
            </div>

            {/* Related Products Section */}
            <RelatedProducts products={relatedData?.items} />
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductGroupDetailPage;
